import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildInputInventory } from '../AreaOverview/hdmiInputInventory';
import { requestRemoteState } from './remoteStateApi';

export function remoteHdmiInputs(deviceId, sourceLayout, report) {
  if (sourceLayout?.televisionDeviceId && Number(sourceLayout.televisionDeviceId) !== Number(deviceId)) return [];
  const knownPorts = new Set(sourceLayout?.knownHdmiPorts || []);
  const screen = {
    activationStrategy: { inputInventory: sourceLayout?.inputInventory },
    players: (sourceLayout?.hdmiSources || []).filter((entry) => knownPorts.has(entry.port))
      .map((entry) => ({ expectedInput: `HDMI ${entry.port}`, name: entry.playerName })),
  };
  const groups = buildInputInventory(screen, report || sourceLayout?.nativeInputInventory);
  const reportedHdmi = groups.some((group) => group.physical && /^HDMI\s*\d+$/i.test(group.input));
  return groups.flatMap((group) => {
    // A numbered physical report determines the button count; old saved labels
    // can enrich those ports but must not invent additional hardware inputs.
    if (reportedHdmi && !group.physical) return [];
    const match = group.input.match(/^HDMI\s*(1[0-6]|[1-9])$/i);
    if (!match) return [];
    return [{ port: Number(match[1]), playerName: group.connections.find((entry) => entry.name)?.name || null,
      reported: Boolean(group.physical) }];
  });
}

export default function useRemoteHdmiInputs({ deviceId, sourceLayout }) {
  const [observation, setObservation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const epoch = useRef(0);
  const pending = useRef(false);
  useEffect(() => {
    epoch.current += 1;
    pending.current = false;
    setObservation(null);
    setLoading(false);
    setMessage('');
    return () => { epoch.current += 1; };
  }, [deviceId]);
  const isTelevision = !sourceLayout?.televisionDeviceId || Number(sourceLayout.televisionDeviceId) === Number(deviceId);
  const refresh = useCallback(async () => {
    if (!deviceId || !isTelevision || pending.current) return;
    const currentEpoch = epoch.current;
    pending.current = true;
    setLoading(true);
    setMessage('');
    try {
      // Ask the selected TV only, with screenshots explicitly off. This is a
      // native inventory observation, not a claim that ADB can inspect inputs.
      const state = await requestRemoteState({ deviceId, includeScreenshot: false });
      if (currentEpoch !== epoch.current) return;
      const report = state?.tvInputManager;
      if (!Array.isArray(report?.inputs) || report.serviceAvailable === false || report.error) throw new Error('input_report_unavailable');
      setObservation({ deviceId, report });
      setMessage('TV input report refreshed. Direct ADB keys are candidates until tested on this TV.');
    } catch {
      if (currentEpoch === epoch.current) setMessage('Live TV inputs unavailable. Showing known inputs; review HDMI ports in Virtual Matrix if needed.');
    } finally {
      if (currentEpoch === epoch.current) { pending.current = false; setLoading(false); }
    }
  }, [deviceId, isTelevision]);
  const sources = useMemo(() => remoteHdmiInputs(deviceId, sourceLayout,
    Number(observation?.deviceId) === Number(deviceId) ? observation?.report : null), [deviceId, sourceLayout, observation]);
  return { sources, refresh, loading, message, isTelevision,
    collectedAt: Number(observation?.deviceId) === Number(deviceId) ? observation?.report?.collectedAt : null };
}
